// Fiche d'une créance de l'usager : objet, collectivité, période, règle de calcul, montants (dû,
// payé, reste), échéance, mention du tarif fictif ; paiement en ligne (montant total ou partiel si
// la rubrique l'admet) qui initie un paiement puis conduit au suivi ; paiements déjà faits.
import { Bandeau, Bouton, Champ, EtatVide, Formulaire } from '@cc/ui';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { Bloc, EnTeteCitoyen, ExigeConnexion, Ligne, Liste, Section, TexteStatut } from '../commun';
import { CLES_PAIEMENTS, payerCreance, useMesCreances, useMesPaiements } from './api';
import { aPayer, francs, libelleEtatCreance, libelleEtatPaiement, montant, paiementsEnAttentePour, tonEtatCreance, tonEtatPaiement } from './logique';
import type { CreanceUsager } from './types';
import styles from '../citoyen.module.css';

function Paiement({ c, reserve }: { c: CreanceUsager; reserve: number }) {
  const naviguer = useNavigate();
  const disponible = Math.max(0, c.reste - reserve);
  const [saisie, setSaisie] = useState(String(disponible));
  const valeur = Number(saisie);
  const partiel = c.rubrique.paiement_partiel_autorise;
  const invalide = !Number.isInteger(valeur) || valeur <= 0 || valeur > disponible || (!partiel && valeur !== disponible);
  const action = useAction({
    executer: () => payerCreance(c.id, valeur === disponible ? {} : { montant: valeur }),
    invalider: [CLES_PAIEMENTS.paiements, CLES_PAIEMENTS.creances],
    apres: (r) => void naviguer(`/citoyen/paiements/suivi/${r.paiement.reference_serveur}`, { state: { url_paiement: r.url_paiement } }),
  });
  if (disponible === 0) {
    return <p className={styles.aide}>Un paiement en attente couvre déjà le reste de cette créance : suivez-le depuis la rubrique Paiements.</p>;
  }
  return (
    <Formulaire libelle="Payer cette créance" onSoumettre={() => !invalide && action.lancer(undefined)} actions={<Bouton type="submit" variante="principal" chargement={action.enCours} disabled={invalide}>Payer {Number.isFinite(valeur) && valeur > 0 ? montant(valeur) : ''}</Bouton>}>
      <Champ
        libelle="Montant à payer (francs CFA)"
        type="number"
        inputMode="numeric"
        min={1}
        max={disponible}
        step={1}
        value={saisie}
        onChange={(e) => setSaisie(e.target.value)}
        aide={partiel ? `Reste à payer : ${francs(disponible)}. Vous pouvez payer en plusieurs fois.` : `Cette créance se paie en une seule fois : ${francs(disponible)}.`}
        erreur={saisie !== '' && invalide ? (partiel ? `Montant entier entre 1 et ${disponible.toLocaleString('fr-FR')} francs` : `Le montant doit être ${francs(disponible)}`) : null}
        disabled={!partiel}
      />
      <p className={styles.aide}>Le paiement est confirmé par le prestataire ; la collectivité en fait foi et délivre la quittance. Aucun argent réel n&apos;est échangé avec le simulateur de présentation.</p>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Formulaire>
  );
}

export function PageCreanceUsager() {
  const { id = '' } = useParams();
  const creances = useMesCreances();
  const paiements = useMesPaiements();
  const c = (creances.data ?? []).find((x) => x.id === id) ?? null;
  const lies = (paiements.data ?? []).filter((p) => p.creances_visees.includes(id));
  const reserve = paiementsEnAttentePour(paiements.data ?? [], id).reduce((s, p) => s + p.montant, 0);
  return (
    <>
      <EnTeteCitoyen titre={c ? c.libelle : 'Créance'} retour="/citoyen/paiements" libelleRetour="Paiements" surTitre={c ? `${c.numero} · ${c.collectivite.nom}` : undefined} />
      <ExigeConnexion titre="Connectez-vous pour voir cette créance">
        <EtatRequete chargement={creances.isPending} erreur={creances.error} onReessayer={() => void creances.refetch()} fonction="Vos créances" lot="L2" lignes={5}>
          {!c ? (
            <EtatVide titre="Créance introuvable" niveau={2}>
              <p>Cette créance n&apos;est pas rattachée à votre compte.</p>
            </EtatVide>
          ) : (
            <>
              <Bloc etiquette={c.rubrique.libelle} titre={aPayer(c) ? `Reste à payer : ${montant(c.reste, c.fictif)}` : libelleEtatCreance(c)}>
                <p className={styles.blocTexte}>
                  <TexteStatut libelle={libelleEtatCreance(c)} ton={tonEtatCreance(c)} />
                  {aPayer(c) ? ` · échéance le ${date(c.echeance)}` : ''}
                </p>
                {c.fictif && c.mention ? <p className={styles.aide}>Montant calculé avec un {c.mention} : aucune somme réelle n&apos;est due.</p> : null}
                {aPayer(c) ? <Paiement c={c} reserve={reserve} /> : null}
              </Bloc>
              <section className={styles.carte} aria-labelledby="detail-creance">
                <h2 id="detail-creance" className={styles.blocTitre}>
                  Détail
                </h2>
                <dl className={styles.definitions}>
                  <dt>Période</dt>
                  <dd>
                    du {date(c.periode.debut)} au {date(c.periode.fin)}
                  </dd>
                  <dt>Règle de calcul</dt>
                  <dd className={styles.mono}>{c.regle || 'non renseignée'}</dd>
                  <dt>Montant dû</dt>
                  <dd className={styles.mono}>
                    {montant(c.montant_du, c.fictif)}
                    {c.montant_remise > 0 ? ` (remise de ${montant(c.montant_remise)} sur ${montant(c.montant_initial)})` : ''}
                  </dd>
                  <dt>Déjà payé</dt>
                  <dd className={styles.mono}>{montant(c.montant_paye)}</dd>
                  <dt>Échéance</dt>
                  <dd>{date(c.echeance)}</dd>
                  {c.motif_annulation ? (
                    <>
                      <dt>Motif de l&apos;annulation</dt>
                      <dd>{c.motif_annulation}</dd>
                    </>
                  ) : null}
                </dl>
              </section>
              <Section titre="Paiements de cette créance" id="paiements-creance">
                {lies.length === 0 ? (
                  <p className={styles.aide}>Aucun paiement enregistré pour cette créance.</p>
                ) : (
                  <Liste libelle="Paiements de cette créance">
                    {lies.map((p) => (
                      <Ligne key={p.id} href={`/citoyen/paiements/suivi/${p.reference_serveur}`} titre={montant(p.montant)} sousTitre={`${p.reference_serveur} · ${date(p.initie_le)}`} droite={<TexteStatut libelle={libelleEtatPaiement(p.etat)} ton={tonEtatPaiement(p.etat)} />} />
                    ))}
                  </Liste>
                )}
              </Section>
            </>
          )}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
